"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import LocalGroceryStoreIcon from "@mui/icons-material/LocalGroceryStore";
import SearchSharpIcon from "@mui/icons-material/SearchSharp";
import s from "./styles";
import logjs from "@metrojs/logjs";
import Consts from "@common/config/consts";
import { useFormEx } from "../../../hooks";
import { useSearchPartsDialogApi } from "./useSearchPartsDialog";

const log = new logjs("SearchPartsDialog");
/*
 * 2026-09-14 tdaig
 * 検索結果は1ページ10件。枠内にスクロールなしで収める
 */
const PAGE_SIZE = 10;
/*
 * 2026-10-07 TanabeDaigo
 * No.24 と同じく、キーワード入力が止まってから検索する
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * 選択済み絞り込みの比較キー。大文字小文字・全角半角・カタカナを揃える
 * 2026-10-06 TanabeDaigo 左の一覧は DB 照合で「ばって」が「バッテリ」に当たる。右側も同じゆれを見る
 * @param {string} value - 入力または部品名
 * @returns {string} 比較キー
 */
function toSelectedFilterKey(value) {
  let text = String(value ?? "");
  try {
    text = text.normalize("NFKC");
  } catch (e) {
    // 不正なサロゲートはそのまま比較する
  }
  text = text.toLowerCase();
  let result = "";
  for (const ch of text) {
    const code = ch.codePointAt(0);
    if (code >= 0x30a1 && code <= 0x30f6) {
      result += String.fromCodePoint(code - 0x60);
    } else {
      result += ch;
    }
  }
  return result;
}

const INIT_FORM = {
  keyword: "",
  daiBunruiCD: "-1",
  chuBunruiCD: "-1",
  selectedFilter: "",
};

const INPUT_STYLE = {
  padding: "3px 5px",
  border: "1px solid #ccc",
  borderRadius: "3px",
  width: "180px",
  height: "32px",
};

/**
 * 部品の一意キーは 4CD（部品名ではない）
 * @param {Object} part - 部品
 * @returns {string} 4CD
 */
function partKey(part) {
  return String(part?.parts4cd ?? part?.["4CD"] ?? part?.["4cd"] ?? "").trim();
}

/**
 * 部品選択ダイアログ（renderDialogEx 用）
 * 2026-09-11 tdaig seller 受注一覧 No.56
 * 2026-09-15 tdaig packages/common へ移設（buyer / seller 共通）
 * @param {Object} props - open, onClose, params
 */
function SearchPartsDialog(props) {
  log.debug("SearchPartsDialog", props);
  const { open, onClose, params } = props;
  const resolvedActions = params?.actions || {};
  const api = useSearchPartsDialogApi(resolvedActions);
  /*
   * 2026-09-15 tdaig
   * 他画面と同様に useFormEx の input / select / button / checkbox を使う
   */
  const [form, formProps] = useFormEx(INIT_FORM, {}, resolvedActions);

  const [daiBunruiList, setDaiBunruiList] = useState([]);
  const [chuBunruiList, setChuBunruiList] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [isLiveReady, setIsLiveReady] = useState(false);
  const searchRequestIdRef = useRef(0);
  const skipNextLiveRef = useRef(false);
  const formRef = useRef(form);
  const runSearchRef = useRef(null);
  formRef.current = form;

  useEffect(() => {
    if (!open) {
      setIsLiveReady(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.init();
        if (cancelled) {
          return;
        }
        setDaiBunruiList(Array.isArray(result?.daiBunruiList) ? result.daiBunruiList : []);
        setChuBunruiList([]);
        formProps.setForm({ ...INIT_FORM });
        setCheckedKeys([]);
        setConfirmType(null);
        /*
         * 2026-09-16 tdaig
         * 都道府県ダイアログと同様、開いたときの選択済みは DB 保存分を正とする
         */
        const savedParts = Array.isArray(result?.selectedParts) ? result.selectedParts : [];
        setSelectedParts(savedParts.map((part) => ({ ...part })));
        /*
         * 2026-09-16 tdaig
         * 開いたときは条件なしの全件検索結果を表示する
         */
        setIsSearching(true);
        const searchResult = await api.search({
          keyword: "",
          daiBunruiCD: "",
          chuBunruiCD: "",
          page: 1,
          pageSize: PAGE_SIZE,
        });
        if (cancelled) {
          return;
        }
        setRows(searchResult.rows);
        setTotal(searchResult.total);
        setPage(searchResult.page);
        setHasSearched(true);
      } catch (error) {
        log.error("init load error", error);
      } finally {
        if (!cancelled) {
          setIsSearching(false);
          /*
           * 2026-10-07 TanabeDaigo
           * 初期の全件表示の直後は、空キーワードでもう一度検索しない
           */
          skipNextLiveRef.current = true;
          setIsLiveReady(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      searchRequestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedKeySet = useMemo(() => new Set(selectedParts.map(partKey).filter(Boolean)), [selectedParts]);

  const filteredSelected = useMemo(() => {
    const q = toSelectedFilterKey(String(form.selectedFilter || "").trim());
    if (q === "") {
      return selectedParts;
    }
    return selectedParts.filter((part) => {
      const hay = toSelectedFilterKey(`${part.partsName || ""} ${part.daiBunruiName || ""} ${part.chuBunruiName || ""} ${part.parts4cd || ""}`);
      return hay.includes(q);
    });
  }, [selectedParts, form.selectedFilter]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const checkedCount = checkedKeys.filter((key) => !selectedKeySet.has(key)).length;

  /**
   * 中分類を読み込む
   * @param {string} nextDai - 大分類コード
   */
  async function handleDaiChange(nextDai) {
    log.debug("START handleDaiChange ---", nextDai);
    formProps.setForm((prev) => ({ ...prev, chuBunruiCD: "-1" }));
    setChuBunruiList([]);
    if (nextDai === "-1") {
      return;
    }
    try {
      const list = await api.getChuBunrui(nextDai);
      setChuBunruiList(list);
    } catch (error) {
      log.error("handleDaiChange error", error);
    }
  }

  /**
   * 部品を検索する
   * @param {number} nextPage - ページ
   */
  async function runSearch(nextPage = 1) {
    log.debug("START runSearch ---", nextPage);
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    const current = formRef.current || {};
    setIsSearching(true);
    setHasSearched(true);
    try {
      const result = await api.search({
        keyword: current.keyword || "",
        daiBunruiCD: current.daiBunruiCD === "-1" ? "" : current.daiBunruiCD,
        chuBunruiCD: current.chuBunruiCD === "-1" ? "" : current.chuBunruiCD,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      if (requestId !== searchRequestIdRef.current) {
        return;
      }
      setRows(result.rows);
      setTotal(result.total);
      setPage(result.page);
      setCheckedKeys([]);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsSearching(false);
      }
    }
  }

  runSearchRef.current = runSearch;

  /*
   * 2026-10-07 TanabeDaigo
   * キーワードが止まって 300ms 後に 1 ページ目を検索する。変換中は送らない。
   * 大・中カテゴリだけ変えても検索しない。検索時はその時点の分類を含める。
   */
  useEffect(() => {
    if (!open || !isLiveReady || isComposing) {
      return;
    }
    if (skipNextLiveRef.current) {
      skipNextLiveRef.current = false;
      if (String(form.keyword || "") === "") {
        return;
      }
    }
    const timer = setTimeout(() => {
      runSearchRef.current(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [open, isLiveReady, isComposing, form.keyword]);

  /**
   * 検索条件をクリアする
   */
  function handleClearConditions() {
    log.debug("START handleClearConditions ---");
    formProps.setForm((prev) => ({
      ...prev,
      keyword: "",
      daiBunruiCD: "-1",
      chuBunruiCD: "-1",
    }));
    setChuBunruiList([]);
  }

  /**
   * 部品を選択済みへ追加する
   * @param {Object} part - 部品
   */
  function addPart(part) {
    const key = partKey(part);
    if (!key || selectedKeySet.has(key)) {
      return;
    }
    setSelectedParts((prev) => [...prev, { ...part, parts4cd: key }]);
  }

  /**
   * チェックした部品を一括追加する
   */
  function addChecked() {
    const toAdd = rows.filter((row) => checkedKeys.includes(partKey(row)) && !selectedKeySet.has(partKey(row)));
    if (toAdd.length === 0) {
      return;
    }
    setSelectedParts((prev) => {
      const next = [...prev];
      const keys = new Set(next.map(partKey));
      toAdd.forEach((row) => {
        const key = partKey(row);
        if (!keys.has(key)) {
          keys.add(key);
          next.push({ ...row, parts4cd: key });
        }
      });
      return next;
    });
    setCheckedKeys([]);
  }

  /**
   * 選択を解除する
   * @param {string} key - 4CD
   */
  function removePart(key) {
    setSelectedParts((prev) => prev.filter((part) => partKey(part) !== key));
  }

  /**
   * 決定を保存して閉じる
   */
  async function handleDecideConfirm() {
    log.debug("START handleDecideConfirm ---");
    setIsSaving(true);
    try {
      const savedParts = await api.save(selectedParts);
      setConfirmType(null);
      onClose({ saved: true, parts: savedParts });
    } catch (error) {
      log.error("handleDecideConfirm error", error);
    } finally {
      setIsSaving(false);
    }
  }

  const allPageChecked = rows.length > 0 && rows.every((row) => checkedKeys.includes(partKey(row)) || selectedKeySet.has(partKey(row)));

  /**
   * ページ全選択を切り替える
   * @param {boolean} checked - チェック状態
   */
  function togglePageCheck(checked) {
    if (checked) {
      const keys = rows.map(partKey).filter(Boolean);
      setCheckedKeys(Array.from(new Set([...checkedKeys, ...keys])));
    } else {
      const pageKeys = new Set(rows.map(partKey));
      setCheckedKeys(checkedKeys.filter((key) => !pageKeys.has(key)));
    }
  }

  const pagerItems = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(pageCount, start + 4);
  for (let i = start; i <= end; i += 1) {
    pagerItems.push(i);
  }

  const daiOptions = [
    { key: "dai-all", value: "-1", label: "すべて" },
    ...daiBunruiList.map((item, index) => ({
      key: `dai-${item.value}-${index}`,
      value: item.value,
      label: item.label,
    })),
  ];
  const chuOptions = [
    { key: "chu-all", value: "-1", label: "すべて" },
    ...chuBunruiList.map((item, index) => ({
      key: `chu-${item.value}-${index}`,
      value: item.value,
      label: item.label,
    })),
  ];

  if (!open) {
    return null;
  }

  return {
    /*
     * 2026-09-16 tdaig
     * タイトル先頭に赤スパナを置く（No.56 ダイアログイメージ）
     */
    title: (
      <s.TitleBar>
        <s.Title>
          <s.TitleWrench aria-hidden="true" />
          部品検索・選択
        </s.Title>
        <s.TitleSelected>
          <LocalGroceryStoreIcon fontSize="small" aria-hidden="true" />
          選択中:<s.TitleSelectedCount>{selectedParts.length}</s.TitleSelectedCount>件
        </s.TitleSelected>
      </s.TitleBar>
    ),
    isModal: true,
    params: {
      size: "xl",
      fullWidth: true,
      maxWidth: false,
      disableEscapeKeyDown: true,
      /*
       * 2026-09-14 tdaig
       * ダイアログ本体は非スクロール。高さは検索結果10件が収まるサイズ。選択済み枠のみ内部スクロール
       * 2026-09-16 tdaig ダイアログを少し大きくし、一覧行高に合わせて高さを確保する
       */
      sx: {
        "& .MuiPaper-root": {
          width: "94vw",
          maxWidth: "1600px",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        "& .MuiDialogContent-root": {
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        },
        /*
         * 2026-09-16 tdaig
         * タイトル右側の選択件数を閉じるボタンのすぐ左に置く
         */
        "& .MuiDialogTitle-root .MuiTypography-root": {
          flex: 1,
          minWidth: 0,
        },
      },
      onClose: (_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          setConfirmType("cancel");
          return;
        }
      },
    },
    content: (
      <s.Wrap>
        <s.Toolbar>
          {/*
           * 2026-09-16 tdaig
           * キーワード入力の先頭に SearchSharp アイコンを表示する
           */}
          <s.KeywordWrap>
            <s.KeywordIcon aria-hidden="true">
              <SearchSharpIcon fontSize="small" />
            </s.KeywordIcon>
            {formProps.input("keyword", {
              is_standard: true,
              placeholder: "部品名やキーワード",
              "aria-label": "部品名やキーワード",
              autoComplete: "off",
              style: { ...INPUT_STYLE, width: "210px", paddingLeft: "28px" },
              onCompositionStart: () => setIsComposing(true),
              onCompositionEnd: () => setIsComposing(false),
              onKeyDown: (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  runSearch(1);
                }
              },
            })}
          </s.KeywordWrap>
          大カテゴリ:
          {formProps.select("daiBunruiCD", {
            is_standard: true,
            "aria-label": "大カテゴリ",
            style: { ...Consts.BASE_STYLE.SELECT, minWidth: "140px", height: "32px" },
            options: daiOptions,
            after_func: async (_f) => {
              await handleDaiChange(_f.daiBunruiCD);
            },
          })}
          中カテゴリ:
          {formProps.select("chuBunruiCD", {
            is_standard: true,
            "aria-label": "中カテゴリ",
            style: { ...Consts.BASE_STYLE.SELECT, minWidth: "140px", height: "32px" },
            options: chuOptions,
          })}
          {formProps.button("find", {
            children: "検索",
            fullwidth: false,
            size: "small",
            disabled: isSearching,
            onClick: () => runSearch(1),
          })}
          {formProps.button("none", {
            children: "条件クリア",
            fullwidth: false,
            size: "small",
            variant: "text",
            onClick: handleClearConditions,
          })}
        </s.Toolbar>

        <s.Body>
          <s.LeftPane>
            <s.PaneHeader>
              <span>
                検索結果一覧
                {hasSearched && <s.CountBadge>{total}</s.CountBadge>}
              </span>
              {formProps.button("none", {
                children: checkedCount > 0 ? `チェックした項目を一括追加 (${checkedCount}件)` : "チェックした項目を一括追加",
                fullwidth: false,
                size: "small",
                disabled: checkedCount === 0,
                onClick: addChecked,
              })}
            </s.PaneHeader>
            <s.TableWrap>
              {!hasSearched ? (
                <s.Empty>検索ボタンを押すと部品一覧を表示します。キーワードや分類でも絞り込めます。</s.Empty>
              ) : rows.length === 0 ? (
                <s.Empty>該当する部品が見つかりませんでした。</s.Empty>
              ) : (
                <s.Table>
                  <thead>
                    <tr>
                      <th>
                        {formProps.checkbox("pageCheckAll", {
                          label: "",
                          is_standard: true,
                          checked: allPageChecked,
                          onChange: (e) => togglePageCheck(e.target.checked),
                          "aria-label": "このページを全選択",
                        })}
                      </th>
                      <th>大分類</th>
                      <th>部位</th>
                      <th>部品名</th>
                      <th>選択</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const key = partKey(row);
                      const isSelected = key !== "" && selectedKeySet.has(key);
                      const checkName = key !== "" ? `rowCheck_${key}` : `rowCheck_i${index}`;
                      return (
                        <tr key={`${key || "empty"}-${row.daiBunruiCD}-${row.chuBunruiCD}-${index}`} className={isSelected ? "selected" : ""}>
                          <td>
                            {formProps.checkbox(checkName, {
                              label: "",
                              is_standard: true,
                              checked: isSelected || checkedKeys.includes(key),
                              disabled: isSelected,
                              onChange: (e) => {
                                if (!key) {
                                  return;
                                }
                                if (e.target.checked) {
                                  setCheckedKeys((prev) => Array.from(new Set([...prev, key])));
                                } else {
                                  setCheckedKeys((prev) => prev.filter((item) => item !== key));
                                }
                              },
                              "aria-label": `${row.partsName} を選択`,
                            })}
                          </td>
                          <td>{row.daiBunruiName}</td>
                          {/*
                           * 2026-09-14 tdaig
                           * 中カテゴリがない部品は「-」を表示する
                           */}
                          <td>{row.chuBunruiName ? row.chuBunruiName : "-"}</td>
                          <td>{row.partsName}</td>
                          <td>
                            {isSelected ? (
                              <s.SelectedMark>選択済</s.SelectedMark>
                            ) : (
                              formProps.button("none", {
                                children: "追加",
                                fullwidth: false,
                                size: "small",
                                onClick: () => addPart(row),
                              })
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </s.Table>
              )}
            </s.TableWrap>
            {hasSearched && total > 0 && (
              <s.Pager>
                <span>
                  {total}件中 {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)}件
                </span>
                {/*
                 * 2026-09-16 tdaig
                 * 現在ページは contained（青）、前後と他ページは outlined（白）
                 * << / >> で先頭・末尾へ移動する
                 */}
                <s.PagerBtns>
                  {formProps.button("none", {
                    children: "<<",
                    fullwidth: false,
                    size: "small",
                    variant: "outlined",
                    disabled: page <= 1 || isSearching,
                    onClick: () => runSearch(1),
                    "aria-label": "先頭のページ",
                  })}
                  {formProps.button("none", {
                    children: "<",
                    fullwidth: false,
                    size: "small",
                    variant: "outlined",
                    disabled: page <= 1 || isSearching,
                    onClick: () => runSearch(page - 1),
                    "aria-label": "前のページ",
                  })}
                  {pagerItems.map((num) => (
                    <React.Fragment key={num}>
                      {formProps.button("none", {
                        children: String(num),
                        fullwidth: false,
                        size: "small",
                        variant: num === page ? "contained" : "outlined",
                        className: num === page ? "current" : "",
                        disabled: isSearching,
                        onClick: () => runSearch(num),
                      })}
                    </React.Fragment>
                  ))}
                  {formProps.button("none", {
                    children: ">",
                    fullwidth: false,
                    size: "small",
                    variant: "outlined",
                    disabled: page >= pageCount || isSearching,
                    onClick: () => runSearch(page + 1),
                    "aria-label": "次のページ",
                  })}
                  {formProps.button("none", {
                    children: ">>",
                    fullwidth: false,
                    size: "small",
                    variant: "outlined",
                    disabled: page >= pageCount || isSearching,
                    onClick: () => runSearch(pageCount),
                    "aria-label": "最後のページ",
                  })}
                </s.PagerBtns>
              </s.Pager>
            )}
          </s.LeftPane>

          <s.RightPane>
            <s.PaneHeader>
              <span>
                選択済み部品
                <s.CountBadge>{selectedParts.length}</s.CountBadge>
              </span>
              {formProps.button("none", {
                children: "一括クリア",
                fullwidth: false,
                size: "small",
                color: "error",
                disabled: selectedParts.length === 0,
                onClick: () => setConfirmType("clear"),
              })}
            </s.PaneHeader>
            <s.SelectedFilterWrap>
              {formProps.input("selectedFilter", {
                is_standard: true,
                placeholder: "選択済み内を検索...",
                "aria-label": "選択済み内を検索",
                autoComplete: "off",
                style: { ...INPUT_STYLE, width: "100%", height: "30px" },
              })}
            </s.SelectedFilterWrap>
            <s.SelectedList>
              {filteredSelected.length === 0 ? (
                <s.Empty>選択中の部品はありません。</s.Empty>
              ) : (
                filteredSelected.map((part) => (
                  <s.SelectedItem key={partKey(part)}>
                    <div>
                      <s.SelectedMeta>
                        大:{part.daiBunruiName || "-"} ・ 中:{part.chuBunruiName || "-"}
                      </s.SelectedMeta>
                      <s.SelectedName>{part.partsName}</s.SelectedName>
                    </div>
                    {formProps.iconButton("delete", {
                      onClick: () => removePart(partKey(part)),
                      title: `${part.partsName} を解除`,
                      "aria-label": `${part.partsName} を解除`,
                    })}
                  </s.SelectedItem>
                ))
              )}
            </s.SelectedList>
            <s.Hint>左一覧の「追加」またはチェック後に一括追加できます。×アイコンで解除できます。</s.Hint>
          </s.RightPane>
        </s.Body>

        <s.Footer>
          <s.FooterNote>※選択した部品は検索条件として保存され、再ログイン後も有効です。</s.FooterNote>
          <s.FooterBtns>
            {formProps.button("close", {
              children: "キャンセル",
              fullwidth: false,
              variant: "outlined",
              onClick: () => setConfirmType("cancel"),
            })}
            {formProps.button("save", {
              children: `決定（${selectedParts.length}件を選択）`,
              fullwidth: false,
              onClick: () => setConfirmType("decide"),
            })}
          </s.FooterBtns>
        </s.Footer>

        {confirmType === "clear" && (
          <s.Overlay>
            <s.ConfirmBox role="dialog" aria-labelledby="parts-clear-title">
              <s.ConfirmTitle id="parts-clear-title">一括解除の確認</s.ConfirmTitle>
              <s.ConfirmText>選択済みの部品をすべて解除します。よろしいですか？</s.ConfirmText>
              <s.ConfirmActions>
                {formProps.button("close", {
                  children: "キャンセル",
                  fullwidth: false,
                  onClick: () => setConfirmType(null),
                })}
                {formProps.button("delete", {
                  children: "すべて解除する",
                  fullwidth: false,
                  color: "error",
                  onClick: () => {
                    setSelectedParts([]);
                    setConfirmType(null);
                  },
                })}
              </s.ConfirmActions>
            </s.ConfirmBox>
          </s.Overlay>
        )}

        {confirmType === "cancel" && (
          <s.Overlay>
            <s.ConfirmBox role="dialog" aria-labelledby="parts-cancel-title">
              <s.ConfirmTitle id="parts-cancel-title">部品選択のキャンセル</s.ConfirmTitle>
              <s.ConfirmText>部品選択をキャンセルして画面を閉じますか？</s.ConfirmText>
              <s.ConfirmText>現在選択中の部品情報は破棄されます。保存されていない変更は失われますのでご注意ください。</s.ConfirmText>
              <s.ConfirmHint>「選択を続ける」を押すと、現在の選択状態のまま編集画面に戻ります。</s.ConfirmHint>
              <s.ConfirmActions>
                {formProps.button("close", {
                  children: "選択を続ける",
                  fullwidth: false,
                  onClick: () => setConfirmType(null),
                })}
                {formProps.button("delete", {
                  children: "変更を破棄して閉じる",
                  fullwidth: false,
                  color: "error",
                  onClick: () => onClose({ saved: false }),
                })}
              </s.ConfirmActions>
            </s.ConfirmBox>
          </s.Overlay>
        )}

        {confirmType === "decide" && (
          <s.Overlay>
            <s.ConfirmBox role="dialog" aria-labelledby="parts-decide-title">
              <s.ConfirmTitle id="parts-decide-title">部品選択の確定</s.ConfirmTitle>
              <s.ConfirmText>
                {selectedParts.length === 0
                  ? "部品条件を解除して検索します。よろしいですか？"
                  : `選択した ${selectedParts.length}件 の部品を検索条件に反映します。よろしいですか？`}
              </s.ConfirmText>
              <s.ConfirmHint>確定後、注文一覧の検索条件として保存され、すぐに一覧へ反映されます。</s.ConfirmHint>
              <s.ConfirmActions>
                {formProps.button("close", {
                  children: "戻る",
                  fullwidth: false,
                  disabled: isSaving,
                  onClick: () => setConfirmType(null),
                })}
                {formProps.button("save", {
                  children: "確定して反映する",
                  fullwidth: false,
                  disabled: isSaving,
                  onClick: handleDecideConfirm,
                })}
              </s.ConfirmActions>
            </s.ConfirmBox>
          </s.Overlay>
        )}
      </s.Wrap>
    ),
  };
}

export default SearchPartsDialog;
